'use client';

import Link from 'next/link';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { forwardRef } from 'react';
import { BartaxyzIcon } from 'xyz-icon-set-react/dist/icons/BartaxyzIcon';
import { useCalendar } from '@/data/sources/calendar';
import type { BreadcrumbItemType } from '@/ui/components/Breadcrumb';
import { Breadcrumb } from '@/ui/components/Breadcrumb';
import { ContextualPopup } from '@/ui/components/ContextualPopup/ContextualPopup';
import { ContextualPopupHeading } from '@/ui/components/ContextualPopup/ContextualPopupHeading';
import { ContextualPopupSection } from '@/ui/components/ContextualPopup/ContextualPopupSection';
import { ContextualPopupWorldMap } from '@/ui/components/ContextualPopup/ContextualPopupWorldMap';
import { Divider } from '@/ui/components/Divider';
import { Typography } from '@/ui/components/Typography';
import { useScrollInteraction } from '@/ui/hooks/useScrollInteraction';
import styles from './PageFrame.module.css';

export interface PageFrameProps {
  breadcrumb?: BreadcrumbItemType[];
}

const Logo: FC = () => <BartaxyzIcon className={styles.logo} />;

const PulsingCircle: FC = () => <div className={styles.pulsingCircle} />;

const MenuItem = forwardRef<
  HTMLDivElement,
  {
    selected: boolean;
    children: ReactNode;
  }
>(({ selected, ...props }, ref) => {
  const { elementRef } = useScrollInteraction(
    {
      domain: [80, 320],
      range: [0, 1],
      clamp: true,
    },
    (element, value) => {
      element.style.backdropFilter = `blur(${value * 10}px)`;
      element.style.WebkitBackdropFilter = `blur(${value * 10}px)`;

      element.style.opacity = `${value}`;
    }
  );

  return (
    <div
      {...props}
      className={styles.menuItem}
      data-selected={selected}
      ref={ref}
    >
      <div className={styles.menuItemBackground} ref={elementRef} />
      {props.children}
    </div>
  );
});

MenuItem.displayName = 'MenuItem';

export const PageFrame: FC<PropsWithChildren<PageFrameProps>> = ({
  children,
  breadcrumb,
}) => {
  const calendar = useCalendar().data;
  const currentCalendarEvent = calendar?.currentEvent;
  const currentCalendarLocation = calendar?.currentLocation;

  return (
    <div>
      <div className={styles.aside} data-variant="left">
        <Link href="/" legacyBehavior={true} passHref={true}>
          <Typography.Link aria-label="Home">
            <Logo />
          </Typography.Link>
        </Link>

        <ContextualPopup behaviour="click" handleComponent={PulsingCircle}>
          {currentCalendarLocation && (
            <>
              <ContextualPopupHeading>
                {currentCalendarLocation.title}
              </ContextualPopupHeading>

              <ContextualPopupWorldMap
                long={currentCalendarLocation.long}
                lat={currentCalendarLocation.lat}
              />
            </>
          )}

          <Divider.Horizontal />

          <ContextualPopupSection>
            <Typography.Caption textAlign="center">
              This widget is connected directly to my Google calendar.{' '}
            </Typography.Caption>
          </ContextualPopupSection>
        </ContextualPopup>

        <Typography.Caption
          className={styles.currentlyWorkingOn}
          size="L"
          fontWeight={600}
        >
          Currently{' '}
          {currentCalendarEvent ? (
            currentCalendarEvent.description ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: currentCalendarEvent.description,
                }}
              />
            ) : (
              <Typography.Link
                accent={true}
                href="https://calendar.google.com/calendar/embed?src=o7fuj1odginv2mhkmpibbinhv8%40group.calendar.google.com&ctz=America%2FToronto"
              >
                {currentCalendarEvent?.summary}
              </Typography.Link>
            )
          ) : currentCalendarEvent === null ? (
            <Typography.Link
              accent={true}
              href="https://calendar.google.com/calendar/embed?src=o7fuj1odginv2mhkmpibbinhv8%40group.calendar.google.com&ctz=America%2FToronto"
            >
              enjoying life
            </Typography.Link>
          ) : (
            <>...</>
          )}
          <sup className={styles.questionMark} />
        </Typography.Caption>
      </div>

      <div className={styles.menu}>
        {[].map(({ href, label }) => (
          <Link href={href} key={href} legacyBehavior={true}>
            <MenuItem
              selected={
                typeof location !== 'undefined' &&
                (location.pathname === href ||
                  location.pathname.startsWith(href))
              }
            >
              <Typography.Caption size="L" fontWeight={600}>
                {label}
              </Typography.Caption>
            </MenuItem>
          </Link>
        ))}
      </div>

      <div className={styles.pageContent}>{children}</div>

      <div className={styles.aside} data-variant="right">
        <Breadcrumb items={breadcrumb!} />
      </div>
    </div>
  );
};
